/**
 * Email API helpers using Vercel server routes + Resend
 */

import { supabase } from "@/lib/supabase";

interface SendEmailPayload {
  to: string;
  subject: string;
  html: string;
  user_id?: string;
  cc?: string[];
}

interface SendEmailResponse {
  success: boolean;
  message_id?: string;
  to?: string;
  error?: string;
}

export const emailApi = {
  /**
   * Send email via Vercel API route backed by Resend
   */
  async sendEmail(payload: SendEmailPayload): Promise<SendEmailResponse> {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        return {
          success: false,
          error: "Na odoslanie emailu sa treba prihlásiť.",
        };
      }

      const response = await fetch("/api/send-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = (await response.json().catch(() => ({}))) as
        | SendEmailResponse
        | { error?: string };

      if (!response.ok) {
        const errorMsg =
          data && typeof data === "object" && "error" in data
            ? String(data.error)
            : `Email API returned ${response.status}`;

        if (
          errorMsg.includes("RESEND_API_KEY") ||
          errorMsg.includes("RESEND_FROM_EMAIL") ||
          errorMsg.includes("not configured")
        ) {
          return {
            success: false,
            error:
              "Email služba nie je nakonfigurovaná. Skontrolujte Resend nastavenia vo Verceli.",
          };
        }

        if (response.status === 401) {
          return {
            success: false,
            error: "Na odoslanie emailu sa treba prihlásiť.",
          };
        }

        return {
          success: false,
          error: errorMsg,
        };
      }

      if (!data || typeof data !== "object") {
        throw new Error("No data returned from email API");
      }

      if ("error" in data && data.error) {
        return {
          success: false,
          error: String(data.error),
        };
      }

      return { success: true, ...data };
    } catch (error) {
      console.error("Email API error:", error);

      let errorMessage = "Failed to send email";

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (
        typeof error === "object" &&
        error !== null &&
        "message" in error
      ) {
        errorMessage = String(error.message);
      } else if (typeof error === "string") {
        errorMessage = error;
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  },
};
